import { Box, Chip, Typography } from '@mui/material'
import CardWithSpace from '../../../components/Cards/CardWithSpace/index.jsx'
import UseTranslation from '../../../hooks/UseTranslation.jsx'
import TitleWithInfo from '../../../components/Cards/helpers/TitleWithInfo.jsx'

const Modules = ({ companyDetails }) => {
  const { t } = UseTranslation()
  const modules = companyDetails?.modules ?? []
  const activeModulesCount = modules.length
  const modulesActiveBadgeLabel = (t?.modulesActiveStatus || '').replace(
    '{{count}}',
    String(activeModulesCount),
  )
  const showApplicantPortalURL = modules.some((m) => m?.code === 'recruitment')

  return (
    <Box sx={styles.sectionGroup}>
      <CardWithSpace customStyles={styles.detailsCardShell}>
        <Box sx={styles.detailsHeaderRow}>
          <Box sx={styles.detailsHeaderTitles}>
            <Typography component="h2" sx={styles.detailsTitle}>
              {t?.['Modules section title']}
            </Typography>
            <Typography sx={styles.detailsSubtitle}>
              {t?.['Active product modules for this company']}
            </Typography>
          </Box>
          <Chip
            label={modulesActiveBadgeLabel}
            size="small"
            sx={styles.countBadgeActive}
          />
        </Box>

        <Box sx={styles.detailsBody}>
          <TitleWithInfo
            title={t?.['Active modules']}
            info={modules.length > 0 ? (
              <Box sx={styles.moduleChipsWrap}>
                {modules.map((m) => (
                  <Chip
                    key={m.id}
                    label={m.name}
                    size="small"
                    sx={styles.moduleBadge}
                  />
                ))}
              </Box>
            ) : (
              null
            )}
            customStyle={styles.detailRow}
          />
          {showApplicantPortalURL && (
            <TitleWithInfo
              title={t?.['Applicant portal URL']}
              info={companyDetails.domain}
              customStyle={styles.detailRow}
            />
          )}
        </Box>
      </CardWithSpace>
    </Box>
  )
}

const styles = {
  sectionGroup: {
    width: '100%',
    mb: { xs: 2.5, sm: 3 },
  },
  sectionOverline: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#92929d',
    mb: 1.25,
    lineHeight: 1.3,
  },
  detailsCardShell: {
    border: '1px solid #e8eaed',
    borderRadius: '12px',
    boxShadow: 'none',
    marginBottom: 0,
    overflow: 'hidden',
    padding: 0,
  },
  detailsHeaderRow: {
    display: 'flex',
    flexDirection: { xs: 'row', sm: 'row' },
    gap: { xs: 0.5, sm: 2 },
    alignItems: { xs: 'flex-start', sm: 'center' },
    px: { xs: 2.5, sm: 3 },
    pt: { xs: 2.5, sm: 3 },
    pb: { xs: 2, sm: 2.5 },
    borderBottom: '1px solid #ebecef',
  },
  detailsHeaderTitles: {
    flex: 1,
    minWidth: 0,
  },
  detailsTitle: {
    fontWeight: 700,
    fontSize: { xs: '1rem', sm: '1.0625rem' },
    color: '#1b2559',
    lineHeight: 1.35,
    letterSpacing: '-0.01em',
  },
  detailsSubtitle: {
    mt: 0.75,
    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
    fontWeight: 400,
    color: '#92929d',
    lineHeight: 1.45,
    maxWidth: '520px',
  },
  countBadgeActive: {
    height: 28,
    flexShrink: 0,
    fontWeight: 600,
    fontSize: '0.75rem',
    color: '#29CC8B',
    backgroundColor: 'rgba(41, 204, 139, 0.12)',
    border: '1px solid #29CC8B',
    borderRadius: '999px',
    alignSelf: { xs: 'flex-start', sm: 'center' },
    '& .MuiChip-label': {
      px: 1.25,
    },
  },
  detailsBody: {
    display: 'flex',
    flexDirection: 'column',
  },
  detailRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 2,
    px: { xs: 2.5, sm: 3 },
    py: { xs: 2, sm: 2.25 },
    borderBottom: '1px solid #ebecef',
    marginRight: '0px !important',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(29, 158, 117, 0.05)',
    },
    ' & h5': {
      flexShrink: 0,
      width: { xs: '100px', sm: '300px' },
      minWidth: { xs: '100px', sm: '300px' },
      maxWidth: { xs: '100px', sm: '300px' },
      fontSize: { xs: '0.8125rem', sm: '0.875rem' },
      fontWeight: 400,
      color: '#92929d',
      lineHeight: 1.4,
    },
    ' & h6': {
      textAlign: { xs: 'right', sm: 'left' },
      width: { xs: '100px', sm: 'unset' },
      fontSize: '14px',
      flex: 1,
      textTransform: 'none',
      wordBreak: 'break-word',
    },
  },
  detailRowTall: {
    alignItems: { xs: 'flex-start', sm: 'flex-start' },
  },
  detailRowLast: {
    borderBottom: 'none',
  },
  moduleChipsWrap: {
    minWidth: 0,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: { xs: 'flex-end', sm: 'flex-start' },
    gap: 1,
    rowGap: 1,
  },
  moduleBadge: {
    height: 30,
    fontWeight: 600,
    fontSize: '0.75rem',
    color: '#29CC8B',
    backgroundColor: 'rgba(41, 204, 139, 0.12)',
    border: '1px solid #29CC8B',
    borderRadius: '999px',
    '& .MuiChip-label': {
      px: 1.25,
    },
  },
}

export default Modules
